import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import Chat from "./components/chat/Chat";

// Components
import Navbar from "./components/common/Navbar";
import LoginForm from "./components/auth/LoginForm";
import RegisterForm from "./components/auth/RegisterForm";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import CheckInOut from "./components/attendance/CheckInOut";
import AttendanceHistory from "./components/attendance/AttendanceHistory";
import AttendanceReport from "./components/admin/AttendanceReport";
import EmployeeManagement from "./components/admin/EmployeeManagement";
import AdminDashboard from "./components/admin/AdminDashboard";
import TaskManagement from "./components/admin/TaskManagement";
import MyTasks from "./components/attendance/MyTasks";
import ActivateAccount from "./components/auth/ActivateAccount";
import { NotificationProvider } from "./context/NotificationContext";
import TeamManagement from "./components/admin/TeamManagement";
import ScreenCaptureListener from "./components/attendance/ScreenCaptureListener";
import PublicStats from "./pages/PublicStats";
import AttendanceChart from "./components/attendance/AttendanceChart";

const getStoredUser = () => {
  try {
    const stored = localStorage.getItem("user");
    if (stored && stored !== "undefined" && stored !== "null") {
      return JSON.parse(stored);
    }
    return null;
  } catch (error) {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    return null;
  }
};

function App() {
  const [user, setUser] = useState(getStoredUser());

  const handleLoginSuccess = (userData) => {
    setUser(userData);
  };

  const handleRegisterSuccess = () => {
    window.location.href = "/login";
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <NotificationProvider user={user}>
        {user && <Navbar user={user} onLogout={handleLogout} />}

        {/* Screen Capture Listener - Only for non-admin users who are logged in */}
        {user && user.role !== "admin" && <ScreenCaptureListener />}

        <div className={user ? "main-content" : ""}>
          <Routes>
            {/* ==================== PUBLIC ROUTES ==================== */}
            <Route
              path="/login"
              element={
                user ? (
                  <Navigate
                    to={user.role === "admin" ? "/admin/dashboard" : "/dashboard"}
                    replace
                  />
                ) : (
                  <LoginForm onLoginSuccess={handleLoginSuccess} />
                )
              }
            />

            <Route
              path="/register"
              element={
                user ? (
                  <Navigate
                    to={user.role === "admin" ? "/admin/dashboard" : "/dashboard"}
                    replace
                  />
                ) : (
                  <RegisterForm onRegisterSuccess={handleRegisterSuccess} />
                )
              }
            />

            <Route
              path="/activate"
              element={
                <ActivateAccount
                  onActivateSuccess={() => (window.location.href = "/login")}
                />
              }
            />

            {/* Public shared stats link (no login required) */}
            <Route path="/public/stats/:token" element={<PublicStats />} />

            {/* ==================== EMPLOYEE ROUTES ==================== */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <CheckInOut />
                </ProtectedRoute>
              }
            />

            <Route
              path="/history"
              element={
                <ProtectedRoute>
                  <AttendanceHistory />
                </ProtectedRoute>
              }
            />

            <Route
              path="/attendance-chart"
              element={
                <ProtectedRoute allowedRoles={["employee", "intern", "student"]}>
                  <AttendanceChart />
                </ProtectedRoute>
              }
            />

            <Route
              path="/my-tasks"
              element={
                <ProtectedRoute>
                  <MyTasks />
                </ProtectedRoute>
              }
            />

            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <Chat />
                </ProtectedRoute>
              }
            />

            {/* ==================== ADMIN ROUTES ==================== */}
            <Route
              path="/admin"
              element={<Navigate to="/admin/dashboard" replace />}
            />

            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/employees"
              element={
                <ProtectedRoute adminOnly={true}>
                  <EmployeeManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/attendance"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AttendanceReport />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/tasks"
              element={
                <ProtectedRoute adminOnly={true}>
                  <TaskManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/teams"
              element={
                <ProtectedRoute adminOnly={true}>
                  <TeamManagement />
                </ProtectedRoute>
              }
            />

            {/* ==================== ROOT & FALLBACK ==================== */}
            <Route
              path="/"
              element={
                <Navigate
                  to={
                    user
                      ? user.role === "admin"
                        ? "/admin/dashboard"
                        : "/dashboard"
                      : "/login"
                  }
                  replace
                />
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </NotificationProvider>
    </Router>
  );
}

export default App;